import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
  CustomModal,
  CustomLoader,
} from "../../../components";
import AxiosInstance, { TokenService } from "../../../services/Api";
import styles from "../plans-pricing/PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/slab-configuration";
const CREATE_API = "/saas/create/slab-configuration";
const UPDATE_API = "/saas/update/slab-configuration";
const VIEW_API = "/saas/view/slab-configuration";

const RANGE_FIELDS = [1, 2, 3, 4];
const RANGE_PLACEHOLDERS = { 1: "500", 2: "1500", 3: "3000", 4: "4500" };

// ✅ String values — "0" is truthy so dropdown never loses Inactive selection
const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const isSuccess = (response) => response?.data?.status === true;

const getUserId = () => {
  const user = TokenService.getUser();
  return user?.id || user?.user_id || 1;
};

const buildInitialFormData = (slab) =>
  RANGE_FIELDS.reduce((acc, n) => {
    acc[`range_${n}`] = slab ? String(slab[`range${n}`] ?? "") : "";
    return acc;
  }, {});

const WarningIcon = () => (
  <svg
    width="70"
    height="70"
    viewBox="0 0 70 70"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Circle — r=32 with cx/cy=35 leaves 3px padding on all sides so stroke never clips */}
    <circle
      cx="35"
      cy="35"
      r="32"
      stroke="#F5821F"
      strokeWidth="2.5"
      fill="none"
    />
    {/* Exclamation stem */}
    <line
      x1="35"
      y1="20"
      x2="35"
      y2="41"
      stroke="#F5821F"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
    {/* Exclamation dot */}
    <circle cx="35" cy="50" r="3" fill="#F5821F" />
  </svg>
);

// ─── Number input guards ──────────────────────────────────────────────────────
const ALLOWED_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Enter",
]);

const handleNumberKeyDown = (e) => {
  if (
    (e.ctrlKey || e.metaKey) &&
    ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase())
  )
    return;
  if (ALLOWED_KEYS.has(e.key)) return;

  if (/^\d$/.test(e.key)) {
    const currentValue = e.target.value || "";
    // ✅ Ranges must be ≥ 1 — block "0" when the field is empty
    if (e.key === "0" && currentValue === "") {
      e.preventDefault();
    }
    return;
  }
  e.preventDefault();
};

const handleNumberWheel = (e) => {
  e.target.blur();
};

// ─────────────────────────────────────────────────────────────────────────────

const CreateSlabConfig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const slabId = location.state?.slabId;
  const isEditMode = Boolean(slabId);

  const [formData, setFormData] = useState(() =>
    buildInitialFormData(undefined),
  );
  const [errors, setErrors] = useState({});
  const [fetchLoading, setFetchLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
  });

  // ── Edit mode only ────────────────────────────────────────────────────────
  const [originalStatus, setOriginalStatus] = useState(null); // string: "1" or "0"
  const [originalRanges, setOriginalRanges] = useState({});
  const [selectedStatus, setSelectedStatus] = useState(null); // { value: string, label }

  // ── fetch slab for edit ───────────────────────────────────────────────────
  const fetchSlabDetails = useCallback(async () => {
    if (!slabId) return;
    setFetchLoading(true);
    try {
      const response = await AxiosInstance.post(VIEW_API, { id: slabId });
      if (isSuccess(response) && response?.data?.data) {
        const slab = response.data.data;
        const fd = buildInitialFormData(slab);

        setFormData(fd);
        setOriginalRanges(fd);

        const stringStatus = String(slab.status);
        const statusOption =
          STATUS_OPTIONS.find((o) => o.value === stringStatus) ??
          STATUS_OPTIONS[0];

        setSelectedStatus(statusOption);
        setOriginalStatus(stringStatus);
      } else {
        throw new Error(
          response?.data?.message ||
            "Failed to fetch slab configuration details",
        );
      }
    } catch (err) {
      setStatusModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching details",
      });
    } finally {
      setFetchLoading(false);
    }
  }, [slabId]);

  useEffect(() => {
    fetchSlabDetails();
  }, [fetchSlabDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = value
      .replace(/[^0-9]/g, "")
      .replace(/^0+/, "")
      .slice(0, 7);
    setFormData((prev) => ({ ...prev, [name]: sanitized }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleStatusChange = (option) => {
    setSelectedStatus(option);
    if (errors.status) setErrors((prev) => ({ ...prev, status: "" }));
  };

  // ── validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};
    let previousValue = -Infinity;
    RANGE_FIELDS.forEach((n) => {
      const key = `range_${n}`;
      const raw = formData[key];
      if (raw === "" || raw === null || raw === undefined) {
        newErrors[key] = `Range ${n} is required`;
        return;
      }
      const numeric = Number(raw);
      if (Number.isNaN(numeric)) {
        newErrors[key] = `Range ${n} must be a number`;
        return;
      }
      // ✅ NEW — ranges must be at least 1 (never 0)
      if (numeric <= 0) {
        newErrors[key] = `Range ${n} must be greater than 0`;
        return;
      }
      if (numeric <= previousValue) {
        newErrors[key] = `Range ${n} must be greater than Range ${n - 1}`;
        return;
      }
      previousValue = numeric;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitClick = () => {
    if (!validate()) return;
    setConfirmModalOpen(true);
  };

  // ── is_active logic ───────────────────────────────────────────────────────
  const computeIsActive = () => {
    const statusChanged =
      selectedStatus !== null &&
      selectedStatus.value !== String(originalStatus);

    const rangeChanged = RANGE_FIELDS.some(
      (n) => formData[`range_${n}`] !== originalRanges[`range_${n}`],
    );

    if (statusChanged) return 1;
    if (rangeChanged) return 0;
    return 0;
  };

  // ── API call ──────────────────────────────────────────────────────────────
  const handleConfirmCreate = async () => {
    setSubmitting(true);
    try {
      const rangePayload = RANGE_FIELDS.reduce((acc, n) => {
        acc[`range_${n}`] = Number(formData[`range_${n}`]);
        return acc;
      }, {});

      let payload;
      let response;

      if (isEditMode) {
        payload = {
          id: slabId,
          ...rangePayload,
          status: Number(selectedStatus?.value ?? originalStatus),
          is_active: computeIsActive(),
          updated_by: getUserId(),
        };
        response = await AxiosInstance.put(UPDATE_API, payload);
      } else {
        payload = {
          ...rangePayload,
          created_by: getUserId(),
        };
        response = await AxiosInstance.post(CREATE_API, payload);
      }

      if (isSuccess(response)) {
        setConfirmModalOpen(false);
        setStatusModal({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            (isEditMode
              ? "Slab configuration updated successfully"
              : "Slab configuration created successfully"),
        });
      } else {
        throw new Error(
          response?.data?.message ||
            `Failed to ${isEditMode ? "update" : "create"} slab configuration`,
        );
      }
    } catch (err) {
      setConfirmModalOpen(false);
      setStatusModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          `Error ${isEditMode ? "updating" : "creating"} slab configuration`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusOk = () => {
    const wasSuccess = statusModal.type === "success";
    setStatusModal((prev) => ({ ...prev, open: false }));
    if (wasSuccess) navigate(LIST_PATH);
  };

  if (fetchLoading) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <CustomLoader text="Loading slab configuration details..." />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Page header ── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>
            {isEditMode
              ? "Edit Slab Configuration"
              : "Create Slab Configuration"}
          </h1>
        </div>

        {isEditMode && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span
              style={{ fontWeight: 600, color: "#374151", fontSize: "15px" }}
            >
              Status :
            </span>
            <div style={{ minWidth: "160px" }}>
              <CustomDropdown
                name="status"
                value={selectedStatus}
                onChange={handleStatusChange}
                options={STATUS_OPTIONS}
                isSearchable={false}
                isClearable={false}
                // disabled={selectedStatus?.label =="Inactive"}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Range fields ── */}
      <div className={styles.card}>
        <div className={styles.formGrid}>
          {RANGE_FIELDS.map((n) => (
            <CustomTextInput
              key={n}
              label={`Range ${n}`}
              name={`range_${n}`}
              type="number"
              value={formData[`range_${n}`]}
              onChange={handleChange}
              onKeyDown={handleNumberKeyDown}
              onWheel={handleNumberWheel}
              placeholder={RANGE_PLACEHOLDERS[n]}
              required
              error={errors[`range_${n}`]}
            />
          ))}
        </div>
      </div>

      <div className={styles.formActions}>
        <CustomButton
          variant="outline-dark"
          fullWidth={false}
          onClick={() => navigate(LIST_PATH)}
          disabled={submitting}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="primary"
          fullWidth={false}
          className={styles.btnOrange}
          onClick={handleSubmitClick}
        >
          Submit
        </CustomButton>
      </div>

      {/* "Are You Sure ?" confirmation dialog */}
      <CustomModal
        isOpen={confirmModalOpen}
        onClose={() => !submitting && setConfirmModalOpen(false)}
        title=""
        size="md"
        showCloseButton={false}
      >
        <div
          style={{
            textAlign: "center",
            padding: "32px 24px 8px",
          }}
        >
          {/* Icon */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            <WarningIcon />
          </div>

          {/* Title */}
          <h2
            style={{
              margin: "0 0 16px",
              fontSize: "26px",
              fontWeight: 700,
              color: "#1A1A2E",
              letterSpacing: "-0.3px",
            }}
          >
            Are You Sure ?
          </h2>

          {/* Subtitle — centered, two lines like screenshot */}
          <p
            style={{
              color: "#797979",
              fontSize: "14px",
              lineHeight: "1.65",
              margin: "0 auto 24px",
              textAlign: "center",
            }}
          >
            Please review the user range slabs before saving this configuration.
            Also, make sure to create the corresponding Add-on Configuration for
            this slab.
          </p>

          {/* Summary list — full-width, left-aligned, matching screenshot */}
          <div
            style={{
              textAlign: "left",
              width: "100%",
              marginBottom: "28px",
            }}
          >
            <p
              style={{
                fontWeight: 600,
                margin: "0 0 8px",
                fontSize: "15px",
                color: "#1A1A2E",
              }}
            >
              Summary:
            </p>
            <ul
              style={{
                margin: 0,
                paddingLeft: "20px",
                color: "#374151",
                fontSize: "14px",
                lineHeight: "2",
              }}
            >
              {RANGE_FIELDS.map((n) => (
                <li key={n}>
                  Range {n}: Up to{" "}
                  {Number(formData[`range_${n}`] || 0).toLocaleString()} users
                </li>
              ))}
            </ul>
            {isEditMode && selectedStatus && (
              <p
                style={{
                  marginTop: "10px",
                  fontSize: "14px",
                  color: "#374151",
                }}
              >
                Status: <strong>{selectedStatus.label}</strong>
              </p>
            )}
          </div>

          {/* Buttons — centred row */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              paddingBottom: "8px",
            }}
          >
            <CustomButton
              variant="outline-dark"
              fullWidth={false}
              onClick={() => setConfirmModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              fullWidth={false}
              className={styles.btnOrange}
              onClick={handleConfirmCreate}
              loading={submitting}
              loadingText={isEditMode ? "Updating..." : "Creating..."}
            >
              {isEditMode ? "Update Slab" : "Create Slab"}
            </CustomButton>
          </div>
        </div>
      </CustomModal>

      {/* ── Success / Error modal ── */}
      <CustomModal
        isOpen={statusModal.open}
        onClose={handleStatusOk}
        title={statusModal.type === "success" ? "Success" : "Failed"}
        size="sm"
      >
        <p className={styles.confirmText}>{statusModal.message}</p>
        <div className={styles.confirmActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={handleStatusOk}
          >
            OK
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default CreateSlabConfig;
